import React from 'react';
import { useTranslation } from 'react-i18next';
import { CheckCircle, Trophy, Award, Lock, Zap } from 'lucide-react';
import { getBadgeSvg } from './badgeConfig';

const rarityColors = {
  common: {
    gradient: 'from-gray-500 to-gray-600',
    bg: 'bg-gray-500/10',
    text: 'text-gray-600 dark:text-gray-400',
    border: 'border-gray-500/30',
    glow: 'shadow-gray-500/10'
  },
  uncommon: {
    gradient: 'from-green-500 to-green-600',
    bg: 'bg-green-100 dark:bg-green-500/10',
    text: 'text-green-700 dark:text-green-400',
    border: 'border-green-500/30',
    glow: 'shadow-green-500/20'
  },
  rare: {
    gradient: 'from-blue-500 to-blue-600',
    bg: 'bg-blue-100 dark:bg-blue-500/10',
    text: 'text-blue-700 dark:text-blue-400',
    border: 'border-blue-500/30',
    glow: 'shadow-blue-500/20'
  },
  epic: {
    gradient: 'from-purple-500 to-purple-600',
    bg: 'bg-purple-100 dark:bg-purple-500/10',
    text: 'text-purple-700 dark:text-purple-400',
    border: 'border-purple-500/30',
    glow: 'shadow-purple-500/20'
  },
  legendary: {
    gradient: 'from-yellow-400 via-yellow-500 to-orange-500',
    bg: 'bg-yellow-500/10',
    text: 'text-yellow-700 dark:text-yellow-400',
    border: 'border-yellow-500/30',
    glow: 'shadow-yellow-500/25'
  },
};

const BadgeCard = ({ badge, isEarned, size = "large" }) => {
  const { t } = useTranslation();

  if (!badge) return null;

  const svgPath = getBadgeSvg(badge);
  const rarity = badge.rarity?.toLowerCase() || 'common';
  const colors = rarityColors[rarity] || rarityColors.common;
  
  // Auto-detect earned state from badge.earnedAt if isEarned prop not provided
  const earned = isEarned !== undefined ? isEarned : !!badge.earnedAt;
  
  const isLocked = !earned && badge.progress !== undefined;
  const progressPercent = badge.total > 0 
    ? Math.min((badge.progress / badge.total) * 100, 100)
    : 0;

  // Size variants
  const sizeClasses = {
    small: {
      container: 'p-4',
      icon: 'w-10 h-10',
      iconContainer: 'p-3',
      title: 'text-base',
      description: 'text-xs',
      badge: 'text-[10px] px-2 py-0.5'
    },
    large: {
      container: 'p-5 sm:p-6',
      icon: 'w-12 h-12 sm:w-14 sm:h-14',
      iconContainer: 'p-3 sm:p-4',
      title: 'text-lg sm:text-xl',
      description: 'text-xs sm:text-sm',
      badge: 'text-xs px-3 py-1'
    }
  };

  const sizes = sizeClasses[size] || sizeClasses.large;

  return (
    <div
      className={`
        relative overflow-hidden rounded-2xl border-2 transition-all duration-300 group
        ${sizes.container}
        ${earned
          ? `border-transparent bg-gradient-to-br ${colors.gradient} shadow-xl ${colors.glow} hover:shadow-2xl hover:scale-[1.02]`
          : 'border-gray-200 dark:border-gray-700/50 bg-white dark:bg-gray-800/50 hover:border-gray-300 dark:hover:border-gray-600/70 hover:bg-gray-50 dark:hover:bg-gray-800/70'
        }
      `}
    >
      {/* Background pattern for earned badges */}
      {earned && (
        <>
          <div className="absolute inset-0 bg-gradient-to-br from-white/5 to-transparent" />
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_70%,rgba(255,255,255,0.1),transparent_50%)]" />
        </>
      )}

      {/* Earned indicator */}
      {earned && (
        <div className="absolute top-3 right-3 z-10 animate-scaleIn">
          <div className="bg-white/20 backdrop-blur-sm rounded-full p-1 shadow-lg">
            <CheckCircle className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
          </div>
        </div>
      )}

      {/* Locked indicator */}
      {isLocked && (
        <div className="absolute top-3 right-3 z-10">
          <div className="bg-gray-900/80 backdrop-blur-sm rounded-full p-1.5 shadow-lg">
            <Lock className="w-4 h-4 sm:w-5 sm:h-5 text-gray-400" />
          </div>
        </div>
      )}

      <div className="relative">
        {/* Header */}
        <div className="flex items-start justify-between mb-4 sm:mb-6">
          {/* Icon */}
          <div
            className={`
              ${sizes.iconContainer} rounded-2xl transition-all duration-300
              ${earned 
                ? 'bg-white/20 shadow-lg backdrop-blur-sm group-hover:bg-white/30' 
                : `${colors.bg} border ${colors.border}`
              }
            `}
          >
            {svgPath ? (
              <img
                src={svgPath}
                alt={badge.name}
                className={`${sizes.icon} object-contain ${!earned && 'opacity-60 grayscale group-hover:grayscale-0 group-hover:opacity-100 transition-all duration-300'}`}
                loading="lazy"
              />
            ) : (
              <Award className={`${sizes.icon} ${earned ? 'text-white' : 'text-gray-500'}`} />
            )}
          </div>

          {/* Rarity badge */}
          <div
            className={`
              ${sizes.badge} rounded-full font-semibold uppercase tracking-wider whitespace-nowrap
              ${earned 
                ? 'bg-white/20 text-white backdrop-blur-sm' 
                : `${colors.bg} ${colors.text} border ${colors.border}`
              }
            `}
          >
            {rarity}
          </div>
        </div>

        {/* Content */}
        <div className="space-y-3 sm:space-y-4">
          {/* Title */}
          <h3
            className={`
              ${sizes.title} font-bold leading-tight
              ${earned ? 'text-white' : 'text-gray-800 dark:text-gray-200'}
            `}
          >
            {badge.name}
          </h3>

          {/* Description */}
          <p
            className={`
              ${sizes.description} leading-relaxed line-clamp-2
              ${earned ? 'text-white/90' : 'text-gray-500 dark:text-gray-400'}
            `}
          >
            {badge.description || t('profileWidgets.badgeCard.defaultDescription')}
          </p>

          {/* Progress bar for locked badges */}
          {isLocked && badge.total > 0 && (
            <div className="space-y-2">
              <div className="flex justify-between items-center text-xs sm:text-sm">
                <span className="text-gray-500 dark:text-gray-400 font-medium">{t('profileWidgets.badgeCard.progress')}</span>
                <span className={`font-bold ${colors.text}`}>
                  {badge.progress} / {badge.total}
                </span>
              </div>
              <div className="relative w-full bg-gray-200 dark:bg-gray-900/50 rounded-full h-2.5 sm:h-3 overflow-hidden border border-gray-300 dark:border-gray-700/50">
                <div
                  className={`h-full rounded-full transition-all duration-500 ease-out bg-gradient-to-r ${colors.gradient}`}
                  style={{ width: `${progressPercent}%` }}
                />
                {/* Progress glow effect */}
                <div
                  className={`absolute top-0 h-full rounded-full blur-sm opacity-50 bg-gradient-to-r ${colors.gradient}`}
                  style={{ width: `${progressPercent}%` }}
                />
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className={`flex items-center justify-between pt-4 sm:pt-5 mt-4 sm:mt-5 border-t ${earned ? 'border-white/10' : 'border-gray-200 dark:border-gray-700/50'}`}>
          {/* Points */}
          <div className="flex items-center gap-2">
            <div className={`p-1.5 rounded-lg ${earned ? 'bg-white/20' : colors.bg}`}>
              <Zap className={`w-4 h-4 sm:w-5 sm:h-5 ${earned ? 'text-yellow-300' : colors.text}`} />
            </div>
            <div>
              <div className={`text-sm sm:text-base font-bold ${earned ? 'text-yellow-300' : colors.text}`}>
                {badge.points || 0}
              </div>
              <div className="text-[10px] sm:text-xs text-gray-500 dark:text-gray-400">{t('profileWidgets.badgeCard.xp')}</div>
            </div>
          </div>

          {/* Earned date */}
          {earned && badge.earnedAt && (
            <div className="text-right">
              <div className="text-xs text-white/80 font-medium">{t('profileWidgets.badgeCard.earned')}</div>
              <div className="text-[10px] sm:text-xs text-white/60">
                {new Date(badge.earnedAt).toLocaleDateString(undefined, {
                  month: 'short',
                  day: 'numeric',
                  year: 'numeric'
                })}
              </div>
            </div>
          )}

          {/* Percentage for locked */}
          {isLocked && (
            <div className="text-right">
              <div className={`text-lg sm:text-xl font-bold ${colors.text}`}>
                {Math.round(progressPercent)}%
              </div>
              <div className="text-[10px] sm:text-xs text-gray-500 dark:text-gray-400">{t('profileWidgets.badgeCard.complete')}</div>
            </div>
          )}
        </div>
      </div>

      <style>{`
        @keyframes scaleIn {
          from { 
            transform: scale(0) rotate(-180deg);
            opacity: 0;
          }
          to { 
            transform: scale(1) rotate(0deg);
            opacity: 1;
          }
        }
        .animate-scaleIn {
          animation: scaleIn 0.5s cubic-bezier(0.34, 1.56, 0.64, 1);
        }
      `}</style>
    </div>
  );
};

export default BadgeCard;